/**
 * Learning Paths Hero - Header section with title, stats, and progress indicator
 */
'use client';
import { CertificateIcon } from '@/components/Icons/Icons';
import CircularProgress from '../common/circular-proress';
import LearningPathEnrollButton from '@/components/common/learning-path-enroll-button';
import { CMSLearningPath } from '@/types/cms-lp';
import { UserLearningPath } from '@/types/learning-paths';
import { useLearningPathCourseProgress } from '@/hooks/useUserLearningPaths';
import { getResumeUrl } from '@/lib/utils/get-resume-url';
import { getCoursesWithQuizCount } from '@/lib/utils/learning-path-counts';
import {
  getProgressDisplayPercent,
  computeLpAssessmentUnlockedFromMergedCourses,
  learningPathShowsCertificatePromo,
} from '@/lib/utils/learning-path-utils';
import { useMemo } from 'react';
import { MarkdownAskAiToolbar } from '@/components/common/MarkdownAskAiToolbar';

export default function LearningPathsHero({
  learningPathsWaysData,
  userLpData,
  markdownLearningPathSlug,
}: {
  learningPathsWaysData: CMSLearningPath;
  userLpData: UserLearningPath | null;
  /** Route segment for `/md/learning-paths/{slug}.md` */
  markdownLearningPathSlug?: string;
}) {
  // Count 1 quiz per course that actually has at least one quiz lesson
  const quizzesCount = getCoursesWithQuizCount(learningPathsWaysData.courses ?? {});

  // Calculate total videos count from all courses, chapters, and lessons
  const videosCount = learningPathsWaysData.courses.course.reduce((total, course) => {
    const courseVideos = course.chapters?.reduce((chapterTotal, chapter) => {
      const lessonVideos = chapter.lessons.lessons.reduce((lessonTotal, lesson) => {
        return lessonTotal + (lesson.video_lesson?.length || 0);
      }, 0);
      return chapterTotal + lessonVideos;
    }, 0);
    return total + courseVideos;
  }, 0);

  // Fetch course progress data to determine resume URL
  const { data: coursesWithProgress } = useLearningPathCourseProgress(
    learningPathsWaysData?.uid,
    learningPathsWaysData?.courses
  );

  // Calculate resume URL based on user progress
  const resumeUrl = useMemo(() => {
    if (!coursesWithProgress) {
      return `/learning-paths/${learningPathsWaysData?.url?.replace('/', '')}`;
    }
    return getResumeUrl({
      learningPathData: learningPathsWaysData,
      userLpData,
      courses: coursesWithProgress,
    });
  }, [learningPathsWaysData, userLpData, coursesWithProgress]);

  const progressPercent = userLpData?.progress?.percentage;
  const hasAssessment = learningPathsWaysData?.is_assessment === true;

  // isCompleted: API only — true when user passed assessment (LP fully done). Do NOT use allCoursesCompleted.
  const isCompleted = userLpData?.progress?.isCompleted === true;
  const canUnlockLpAssessment = computeLpAssessmentUnlockedFromMergedCourses(
    userLpData,
    coursesWithProgress ?? []
  );

  // Progress bar: 100% when assessment unlocked (course/LP rules) or LP fully completed
  const displayProgressPercent = isCompleted || canUnlockLpAssessment ? 100 : progressPercent;

  return (
    <div className="flex flex-row items-start justify-between md:items-center">
      <div className="w-[70%]">
        <h1
          className="cs-h1-3xl text-text-default-strong mb-4"
          dangerouslySetInnerHTML={{ __html: learningPathsWaysData?.title }}
        ></h1>
        <div className="cs-m-body text-text-default-strong mb-8 whitespace-break-spaces">
          {typeof learningPathsWaysData?.learning_pathway_details?.description === 'string'
            ? learningPathsWaysData.learning_pathway_details.description.trim()
            : learningPathsWaysData?.learning_pathway_details?.description}
        </div>

        <div className="mt-6 flex flex-col items-start gap-8 lg:flex-row lg:items-center">
          <LearningPathEnrollButton
            learningPathUid={learningPathsWaysData?.uid}
            initialIsSubscribed={userLpData?.isSubscribed ?? false}
            initialHasStarted={userLpData?.progress?.hasStarted ?? false}
            progressPercent={displayProgressPercent}
            isCompleted={isCompleted}
            isLearningMaterialCompleted={canUnlockLpAssessment}
            hasAssessment={hasAssessment}
            navigateUrl={resumeUrl}
            variant="PrimaryFillButton"
            directHover
            className="btn-icon-move min-w-[120px] justify-center"
            buttonText={{ enrolledAndStarted: 'Resume Learning' }}
          />

          {/* Stats */}
          <div className="flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-4">
            {/* Certificate Badge */}
            {learningPathShowsCertificatePromo(
              learningPathsWaysData.learning_pathway_details?.awards_certifications
            ) && (
              <>
                <div className="mb-5 flex flex-row flex-nowrap items-center gap-2 whitespace-nowrap md:mb-0">
                  <CertificateIcon
                    className="text-[var(--Text-Default-Light)]"
                    width={20}
                    height={20}
                    opacity={0.67}
                  />
                  <span className="cs-caption">Certificate</span>
                </div>
              </>
            )}

            {/* Courses */}
            {learningPathsWaysData.courses.course.length !== 0 && (
              <>
                <span className="cs-caption">
                  <span className="text-text-default-medium">{`${learningPathsWaysData.courses.course.length}`}</span>{' '}
                  Course{learningPathsWaysData.courses.course.length > 1 ? 's' : ''}
                </span>
              </>
            )}

            {/* Videos */}
            {videosCount > 0 && (
              <>
                <div className="hidden md:block">
                  <span className="divider" />
                </div>
                <span className="horizontal-divider sm:block md:hidden"></span>
                <span className="cs-caption">
                  <span className="text-text-default-medium">{videosCount}</span> Video
                  {videosCount !== 1 ? 's' : ''}
                </span>
                <div className="hidden md:block">
                  <span className="divider" />
                </div>
                <span className="horizontal-divider sm:block md:hidden"></span>
              </>
            )}

            {/* Quizzes */}
            {quizzesCount !== 0 && (
              <>
                <span className="cs-caption">
                  <span className="text-text-default-medium">{quizzesCount}</span> Quiz
                  {quizzesCount > 1 ? 'zes' : ''}
                </span>
                {quizzesCount !== undefined && (
                  <>
                    <div className="hidden md:block">
                      <span className="divider" />
                    </div>
                    <span className="horizontal-divider sm:block md:hidden"></span>
                  </>
                )}
              </>
            )}

            {/* Assessment */}
            {learningPathsWaysData.is_assessment === true && (
              <span className="cs-caption">
                <span className="text-text-default-medium">1</span> Assessment
              </span>
            )}

            {markdownLearningPathSlug ? (
              <>
                <div className="hidden md:block">
                  <span className="divider" />
                </div>
                <span className="horizontal-divider sm:block md:hidden"></span>
                <MarkdownAskAiToolbar
                  spec={{ kind: 'learning_path', learningPath: markdownLearningPathSlug }}
                  variant="inline"
                />
              </>
            ) : null}
          </div>
          {/* <div className="cs-s-body ml-auto flex items-center gap-2.5">
            Feedback
            <ExternalLinkIcon />
          </div> */}
        </div>
      </div>
      {userLpData?.progress.hasStarted || userLpData?.isSubscribed ? (
        <div className="flex w-[30%] items-center justify-center">
          <CircularProgress
            progress={Math.round(getProgressDisplayPercent(displayProgressPercent, isCompleted))}
          />
        </div>
      ) : null}
    </div>
  );
}
